import React from 'react';
import { Check } from 'lucide-react';
import clsx from 'clsx';
import Button from '@components/common/Button';
import Select from '@components/common/Select';
import { Checkbox, Input, DateInput } from '@components/common';
import styles from './GenericForm.module.css';

export interface FormField {
  name: string;
  label: string;
  type: 'text' | 'email' | 'password' | 'date' | 'select' | 'multiselect' | 'checkbox' | 'readonly' | 'textarea';
  required?: boolean;
  options?: { value: string; label: string }[];
  placeholder?: string;
  disabled?: boolean;
  hidden?: boolean;
  /** Date fields only: overrides the default calendar bounds (YYYY-MM-DD). */
  min?: string;
  max?: string;
  validation?: (value: unknown, formData?: Record<string, unknown>) => string | undefined | Promise<string | undefined>;
}

/**
 * Default calendar bounds for date fields.
 *
 * A native date input accepts any year it can represent, so without bounds the
 * spinner silently walks to years like 0001 or 275760. These match the API's
 * configured contract-date range, so the field and the server agree (ADO-1097).
 */
const DATE_FIELD_MIN = '1900-01-01';
const DATE_FIELD_MAX = '2100-12-31';

type FormDataValue = string | string[] | boolean;
type FormDataRecord = Record<string, FormDataValue>;

// Helper: Update auth_source based on role_ids changes
const updateAuthSourceForRoleIds = (newData: FormDataRecord, value: FormDataValue, fields: FormField[]): void => {
  const authField = fields.find((f) => f.name === 'auth_source');
  if (authField?.type !== 'readonly') return;

  const hasRoles = Array.isArray(value) && value.length > 0;
  newData.auth_source = hasRoles ? 'SSO' : 'None';

  if (!hasRoles) {
    newData.can_login = 'false';
  }
};

// Helper: Update auth_source based on legacy role_id changes
const updateAuthSourceForRoleId = (newData: FormDataRecord, value: FormDataValue, fields: FormField[]): void => {
  const authField = fields.find((f) => f.name === 'auth_source');
  if (authField?.type !== 'readonly') return;

  newData.auth_source = value && value !== '' ? 'SSO' : 'None';

  if (!value || value === '') {
    newData.can_login = 'false';
  }
};

// Helper: Apply field-specific updates when form data changes
const applyFieldSpecificUpdates = (
  name: string,
  value: FormDataValue,
  newData: FormDataRecord,
  fields: FormField[]
): void => {
  if (name === 'role_ids') {
    updateAuthSourceForRoleIds(newData, value, fields);
    return;
  }

  if (name === 'auth_source' && (value === 'None' || value === 'none')) {
    newData.can_login = 'false';
    return;
  }

  if (name === 'role_id') {
    updateAuthSourceForRoleId(newData, value, fields);
    return;
  }

  if (name === 'is_active' && value === 'false') {
    newData.can_login = 'false';
  }
};

// Helper: Validate required fields
const validateRequiredFields = (fields: FormField[], formData: FormDataRecord): Record<string, string> => {
  const errors: Record<string, string> = {};

  fields.forEach((field) => {
    if (field.required && !formData[field.name] && formData[field.name] !== false) {
      errors[field.name] = `${field.label} is required`;
    }
  });

  return errors;
};

// Helper: Run async validations for a single field
const validateField = async (field: FormField, formData: FormDataRecord): Promise<string | null> => {
  if (!field.validation || !formData[field.name]) {
    return null;
  }

  try {
    const error = await field.validation(formData[field.name], formData);
    if (error) {
      return typeof error === 'string' ? error : String(error);
    }
    return null;
  } catch (err) {
    const error = err as Error;
    return error?.message || 'Validation error';
  }
};

// Helper: Run all async validations
const validateAllFields = async (fields: FormField[], formData: FormDataRecord): Promise<Record<string, string>> => {
  const errors: Record<string, string> = {};

  for (const field of fields) {
    const error = await validateField(field, formData);
    if (error) {
      errors[field.name] = error;
    }
  }

  return errors;
};

// Helper: Toggle multiselect value
const toggleMultiselectValue = (
  currentValues: string[],
  optionValue: string,
  isSelected: boolean,
  isAdminOption: boolean
): string[] => {
  if (isAdminOption) {
    return isSelected ? [] : [optionValue];
  }

  if (isSelected) {
    return currentValues.filter((v) => v !== optionValue);
  }

  return [...currentValues, optionValue];
};

// Component: Render multiselect field
interface MultiselectFieldProps {
  field: FormField;
  formData: FormDataRecord;
  handleChange: (name: string, value: FormDataValue) => void;
}

const MultiselectField: React.FC<MultiselectFieldProps> = ({ field, formData, handleChange }) => {
  const selectedValues = Array.isArray(formData[field.name]) ? (formData[field.name] as string[]) : [];

  const adminOption = field.name === 'role_ids' ? field.options?.find((o) => o.label === 'Admin') : null;
  const isAdminSelected = adminOption && selectedValues.includes(adminOption.value);

  return (
    <div
      id={field.name}
      className={clsx(styles.togglePillsContainer, field.disabled && styles.togglePillsContainerDisabled)}
    >
      <div className={styles.togglePills}>
        {(field.options || []).map((option) => {
          const isSelected = selectedValues.includes(option.value);
          const isAdminOption = option.label === 'Admin';
          const isDisabledByAdmin = isAdminSelected && !isAdminOption;

          return (
            <button
              key={option.value}
              type="button"
              className={clsx(
                styles.togglePill,
                isSelected && styles.togglePillSelected,
                isDisabledByAdmin && styles.togglePillDisabledByAdmin
              )}
              disabled={field.disabled || isDisabledByAdmin || false}
              onClick={() => {
                const newValues = toggleMultiselectValue(selectedValues, option.value, isSelected, isAdminOption);
                handleChange(field.name, newValues);
              }}
            >
              {isSelected && <Check size={14} className={styles.togglePillCheck} />}
              {option.label}
            </button>
          );
        })}
      </div>
      {(!formData[field.name] || (Array.isArray(formData[field.name]) && selectedValues.length === 0)) &&
        field.placeholder && <div className={styles.togglePillsHint}>{field.placeholder}</div>}
    </div>
  );
};

// Component: Render form field based on type
interface FormFieldRendererProps {
  field: FormField;
  formData: FormDataRecord;
  allErrors: Record<string, string>;
  handleChange: (name: string, value: FormDataValue) => void;
}

const FormFieldRenderer: React.FC<FormFieldRendererProps> = ({ field, formData, allErrors, handleChange }) => {
  if (field.type === 'select') {
    return (
      <Select
        id={field.name}
        value={String(formData[field.name] || '')}
        onChange={(value) => handleChange(field.name, value)}
        options={field.options || []}
        placeholder={field.placeholder}
        disabled={field.disabled}
        error={allErrors[field.name]}
      />
    );
  }

  if (field.type === 'multiselect') {
    return <MultiselectField field={field} formData={formData} handleChange={handleChange} />;
  }

  if (field.type === 'checkbox') {
    return (
      <Checkbox
        id={field.name}
        label={field.placeholder || 'Enable'}
        checked={!!formData[field.name]}
        onChange={(e) => handleChange(field.name, e.target.checked)}
        disabled={field.disabled}
      />
    );
  }

  if (field.type === 'readonly') {
    return (
      <>
        <div className={styles.readonlyField} data-test-id={`readonly-${field.name}`}>
          {formData[field.name] || '-'}
        </div>
        {field.placeholder && <p className={styles.readonlyHint}>{field.placeholder}</p>}
      </>
    );
  }

  if (field.type === 'date') {
    return (
      <DateInput
        id={field.name}
        type="date"
        value={String(formData[field.name] || '')}
        onChange={(e) => handleChange(field.name, e.target.value)}
        placeholder={field.placeholder}
        disabled={field.disabled}
        min={field.min ?? DATE_FIELD_MIN}
        max={field.max ?? DATE_FIELD_MAX}
        error={allErrors[field.name]}
      />
    );
  }

  return (
    <Input
      type={field.type}
      id={field.name}
      value={String(formData[field.name] || '')}
      onChange={(e) => handleChange(field.name, e.target.value)}
      placeholder={field.placeholder}
      disabled={field.disabled}
      error={allErrors[field.name]}
    />
  );
};

interface GenericFormProps<T> {
  fields: FormField[];
  initialData?: Partial<T>;
  onSubmit: (data: T) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  submitLabel?: string;
  title?: string;
  onFieldChange?: (name: string, value: FormDataValue) => void;
  submitDisabled?: boolean;
  externalErrors?: Record<string, string>;
  /** When true, hides the built-in Cancel/Submit buttons. Use with formRef to trigger submit externally. */
  hideActions?: boolean;
  /** Ref to the form element, allowing external buttons to trigger submission. */
  formRef?: React.Ref<HTMLFormElement>;
  /** Extra content rendered after the fields but before the Cancel/Submit actions. */
  children?: React.ReactNode;
  /** Merge these values into form state (e.g. auto-set Assessment Scope to Calculate). */
  externalValues?: Partial<FormDataRecord>;
}

function GenericForm<T>({
  fields,
  initialData = {},
  onSubmit,
  onCancel,
  isSubmitting,
  submitLabel = 'Save',
  title,
  onFieldChange,
  submitDisabled = false,
  externalErrors = {},
  hideActions = false,
  formRef,
  children,
  externalValues
}: Readonly<GenericFormProps<T>>) {
  const [formData, setFormData] = React.useState<FormDataRecord>(initialData as FormDataRecord);
  const [errors, setErrors] = React.useState<Record<string, string>>({});

  React.useEffect(() => {
    if (!externalValues) {
      return;
    }

    setFormData((prev) => {
      const next: FormDataRecord = { ...prev };
      let changed = false;
      for (const [key, value] of Object.entries(externalValues)) {
        if (value === undefined || prev[key] === value) {
          continue;
        }
        next[key] = value;
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [externalValues]);

  // Merge external errors with internal errors
  const allErrors = { ...errors, ...externalErrors };

  const handleChange = (name: string, value: FormDataValue) => {
    setFormData((prev) => {
      const newData = { ...prev, [name]: value };
      applyFieldSpecificUpdates(name, value, newData, fields);
      return newData;
    });

    if (onFieldChange) {
      onFieldChange(name, value);
    }

    // Clear error on change
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const requiredErrors = validateRequiredFields(fields, formData);
    const validationErrors = await validateAllFields(fields, formData);
    const allValidationErrors = { ...requiredErrors, ...validationErrors };

    if (Object.keys(allValidationErrors).length === 0) {
      onSubmit(formData as T);
    } else {
      setErrors(allValidationErrors);
    }
  };

  return (
    <div className={styles.genericFormContainer}>
      {title && <h3>{title}</h3>}
      <form ref={formRef} onSubmit={handleSubmit} className={clsx(styles.genericForm, 'generic-form')}>
        {fields
          .filter((field) => !field.hidden)
          .map((field) => (
            <div key={field.name} className={clsx(styles.formGroup, 'form-group')}>
              {field.label && (
                <label htmlFor={field.name} className={styles.formLabel}>
                  {field.label} {field.required && <span className={styles.required}>*</span>}
                </label>
              )}
              <FormFieldRenderer field={field} formData={formData} allErrors={allErrors} handleChange={handleChange} />
            </div>
          ))}

        {children}

        {!hideActions && (
          <div className={clsx(styles.formActions, 'form-actions')}>
            <Button type="button" variant="secondary" onClick={onCancel} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={isSubmitting} disabled={submitDisabled || isSubmitting}>
              {submitLabel}
            </Button>
          </div>
        )}
      </form>
    </div>
  );
}

export default GenericForm;
